import React from "react"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"

import BrowserCompatibilityWarning from "./modules/mp-utils/ready-utils/browser-compatibility-warning"

import { Box } from "@mui/material"
import { LicenseInfo } from "@mui/x-license"

import { SnackbarProvider } from "notistack"

import AnnouncementsBannerContainer from "./modules/mp-core/components/AnnouncementsBannerContainer"
import DocumentProvider from "./modules/mp-shared-components/ready-shared-components/components/DocumentModal/DocumentProvider"
import { TermsOfServiceProvider } from "./modules/mp-shared-components/ready-shared-components/components/TermsOfServiceModal"

import getActiveOrExpiredLicenses from "./modules/mp-core/helpers/users/licenses/getActiveorExpiredLicenses"
import {
  checkLogin,
  checkAnnouncements,
  setConnectStatus
} from "./modules/mp-core/redux/modules/users"
import {
  getUserLicenseRequest,
  getUserLicenseFailure,
  getUserLicenseSuccess
} from "./modules/mp-core/redux/modules/license"
import { testSettingActions } from "./modules/mp-core/redux/actions/testSettingActions"
import { assessmentActions } from "./modules/mp-core/redux/actions/assessmentActions"
import educatorStartModeTrigger from "./modules/mp-core/helpers/assessment/educatorStartModeTrigger"

import { toast } from "react-toastify"
import { isEqual } from "lodash"
import { PulseLoader } from "react-spinners"
import Router from "./pages/Router"
import { AnnounceProvider } from "@masteryprep/mp-shared-components/generic"
import { AppProvider } from "./modules/mp-core/context"
import { MpLoader } from "./modules/mp-shared-components/ready-shared-components/components/mpLoader"
import { Snackbar } from "./modules/mp-shared-components/generic-shared-components/components"
import {
  PlatformSettingsProvider,
  ProgramsProvider
} from "./modules/mp-shared-components/ready-shared-components/components/Navs"
import SessionTimeoutTimer from "./modules/mp-core/SessionTimeoutTimer"
import MosChatManager from "./modules/mp-core/MosChatManager"

import { isProgramFromStudyPlan } from "./modules/eoc/dashboard/helpers"
import { FeatureFlagProvider } from "./modules/mp-core/FeatureFlagProvider"
import { EventChainProvider } from "./modules/mp-utils/generic-utils/Events/EventChainProvider"
import {
  getPreviousSSOSource,
  removePreviousSSOSource
} from "./modules/login/LoggedOutSSO/helpers"
import { initClarity } from "./utils/clarity"
import { capturePostLoginRedirect } from "@masteryprep/mp-shared-components/utils"
import { checkFeatureFlag } from "./modules/mp-utils/generic-utils/feature-flags"
import { FeatureFlagName } from "./modules/mp-utils/generic-utils/feature-flags/types"

const authedRoutes = [
  "/truscore",
  "/mastery",
  "/ebooks",
  "/manage",
  "/reports",
  "/school-support",
  "/elements",
  "/snap",
  "/profile",
  "/context-help-manager",
  "/help",
  "/integrations",
  "/eoc",
  "/reporting",
  "/student-experience"
]

const SNACKBAR_COMPONENTS = {
  default: Snackbar,
  success: Snackbar,
  error: Snackbar,
  warning: Snackbar,
  info: Snackbar
}

toast.configure()
class App extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showBrowserCompatibilityWarnings: true,
      checkAnnouncementsIntervalId: null,
      initialLoad: true,
      loadingAssessment: false
    }
    this.subscriptions = []
  }

  componentDidMount() {
    const {
      history,
      user,
      checkLogin,
      _sendStatus,
      onlineListener,
      offlineListener
    } = this.props

    // Make sure Firebase has been initialized before proceeding with auth operations
    // The actual initialization happens in src/modules/mp-database/firebaseInit.ts
    // which is imported at the top of src/index.tsx
    console.log("App mounted - Firebase should already be initialized")

    capturePostLoginRedirect(history)

    this.props.checkAnnouncements(user || {})
    if (
      history.location.pathname !== "/oauth/clever" &&
      history.location.pathname !== "/oauth/classlink"
    ) {
      this.subscriptions.push(checkLogin())
    }

    window.addEventListener("online", onlineListener)
    window.addEventListener("offline", offlineListener)

    if (user) {
      this.setState({
        checkAnnouncementsIntervalId: setInterval(
          () => this.props.checkAnnouncements(user || {}),
          60 * 5000
        )
      })
    }

    // MUI License setting
    LicenseInfo.setLicenseKey(process.env.VITE_MUI_LICENSE_KEY)
  }

  componentDidUpdate(prevProps) {
    const { isAuthed, isAuthing, user, history, userLicenses } = this.props

    if (
      prevProps.isAuthed !== isAuthed &&
      isAuthed === false &&
      authedRoutes.find(route => history.location.pathname.includes(route))
    ) {
      const previousSSOSource = getPreviousSSOSource()

      if (!!previousSSOSource) {
        removePreviousSSOSource()

        history.push(`/logged-out/${previousSSOSource}`)
      } else {
        history.push("/login")
      }
    }

    if (
      (!isEqual(user, prevProps.user) ||
        (isAuthing !== prevProps.isAuthing && isAuthing === false)) &&
      isAuthed
    ) {
      if (user) {
        getActiveOrExpiredLicenses(user, {
          getUserLicenseRequest: this.props.getUserLicenseRequest,
          getUserLicenseFailure: this.props.getUserLicenseFailure,
          getUserLicenseSuccess: this.props.getUserLicenseSuccess
        })

        this.props.checkAnnouncements(user || {})
      }

      // Initialize Microsoft Clarity tracking for eligible users.
      // checkFeatureFlag is used directly here because App is the parent of
      // FeatureFlagProvider and cannot consume its context.
      if (user && user.id && user.role) {
        checkFeatureFlag(FeatureFlagName.enable_microsoft_clarity, {
          userId: user.email
        })
          .then(flag => {
            initClarity(
              user,
              import.meta.env.VITE_APPLICATION_ID,
              flag.enabled,
              window.location.pathname
            )
          })
          .catch(err =>
            console.warn("[Clarity] Feature flag check failed:", err)
          )
      }
    }

    if (
      !isEqual(userLicenses, prevProps.userLicenses) &&
      user &&
      user.role &&
      isAuthed
    ) {
      this.props.getAssessmentSets(userLicenses, user.role)
    }

    const { activeTestSettings } = this.props.testSettings

    if (activeTestSettings && activeTestSettings.modes) {
      const {
        user,
        history,
        assessment: { inTest, StudentInTest }
      } = this.props
      const { modes } = activeTestSettings
      const { initialLoad } = this.state
      const prevModeCheck =
        prevProps.testSettings &&
        prevProps.testSettings.activeTestSettings.modes

      if (user && user.role === "Student") {
        if (initialLoad && prevProps.testSettings !== this.props.testSettings) {
          this.setState({ initialLoad: false })
        }

        if (
          !initialLoad &&
          !inTest &&
          !StudentInTest &&
          prevModeCheck &&
          prevModeCheck.educatorStartMode !== modes.educatorStartMode &&
          userLicenses?.length
        ) {
          const { educatorStartMode } = modes

          if (
            educatorStartMode &&
            educatorStartMode.enabled &&
            educatorStartMode.settings
          ) {
            const educatorStartModeAssessmentId =
              educatorStartMode.settings.assessmentId

            const educatorStartModeLicense = userLicenses.find(
              license => license.licenseSet === educatorStartModeAssessmentId
            )

            const educatorStartModeProgram =
              educatorStartModeLicense?.navName?.split(" ")?.[0]

            const isEducatorStartModeProgramConnectToStudyPlan =
              isProgramFromStudyPlan(educatorStartModeProgram, userLicenses)

            const mpCurrentProgram =
              sessionStorage.getItem("mp_current_program")

            const isCurrentProgramConnectToStudyPlan = isProgramFromStudyPlan(
              mpCurrentProgram,
              userLicenses
            )

            // Condition was added for filtering programs that can not have educator start mode flag.
            // Study plan programs can't support educator start mode,
            // that's why if program is study plan program, then we ignore `educatorStartModeTrigger` method
            if (
              isCurrentProgramConnectToStudyPlan ||
              !educatorStartModeProgram ||
              isEducatorStartModeProgramConnectToStudyPlan
            )
              return

            educatorStartModeTrigger(
              educatorStartMode,
              user,
              history,
              this.setState.bind(this),
              this.props.initAssessment,
              userLicenses
            )
          }
        }
      }
    }
  }

  componentWillUnmount() {
    const { checkAnnouncementsIntervalId } = this.state
    const { onlineListener, offlineListener } = this.props

    if (checkAnnouncementsIntervalId) {
      clearInterval(checkAnnouncementsIntervalId)
    }

    if (this.subscriptions.length) {
      this.subscriptions.forEach(unsub => unsub())
    }

    window.removeEventListener("online", onlineListener)
    window.removeEventListener("offline", offlineListener)
  }

  setSubscriptions = newSubscription => {
    this.subscriptions = [...this.subscriptions, newSubscription]
  }

  render() {
    const { showBrowserCompatibilityWarnings, loadingAssessment } = this.state
    const {
      user,
      isAuthed,
      isFetching,
      userLicenses,
      fetchingLicenses,
      assessment,
      isAuthing
    } = this.props

    // render
    return (
      <AppProvider isAuthed={isAuthed}>
        <MosChatManager
          user={user}
          isAuthed={isAuthed}
          isAuthing={isAuthing}
        />
        {(showBrowserCompatibilityWarnings && (
          <BrowserCompatibilityWarning
            checkUpdates={true}
            checkResponsiveness={true}
          />
        )) ||
          null}

        <SnackbarProvider
          Components={SNACKBAR_COMPONENTS}
          maxSnack={3}
          autoHideDuration={3000}
          anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        />

        {(isAuthed && !isFetching && user && loadingAssessment && (
          <div className="start-test-overlay">
            <div className="overlay-content">
              <h2>Starting test</h2>{" "}
              <PulseLoader loading color="#4DAF7C" size={20} margin="10px" />
            </div>
          </div>
        )) ||
          null}

        {isFetching ? (
          <Box width={1} height={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={"100vh"}
            >
              <MpLoader marginTop={0} />
            </Box>
          </Box>
        ) : (
          <ProgramsProvider
            user={user}
            isAuthed={isAuthed}
            userLicenses={userLicenses}
          >
            <PlatformSettingsProvider user={user}>
              <AnnouncementsBannerContainer
                placement="TOP"
                excludeRoutes={["/reporting/*", "/student-experience/*"]}
              />

              <FeatureFlagProvider>
                <EventChainProvider>
                  <AnnounceProvider>
                    <DocumentProvider key={isAuthed ? user?.id : "signed-out"}>
                      <TermsOfServiceProvider>
                        <Router
                          user={user}
                          isAuthed={isAuthed}
                          isFetching={isFetching || fetchingLicenses || isAuthing}
                          userLicenses={userLicenses}
                          assesmentSets={assessment.assessmentSets}
                        />
                        <SessionTimeoutTimer isAuthed={isAuthed} />
                      </TermsOfServiceProvider>
                    </DocumentProvider>
                  </AnnounceProvider>
                </EventChainProvider>
              </FeatureFlagProvider>
            </PlatformSettingsProvider>
          </ProgramsProvider>
        )}
      </AppProvider>
    )
  }
}
const mapStateToProps = state => {
  return {
    user: state.users.testUser,
    isAuthing: state.users.isAuthing,
    isFetching: state.users.isFetching,
    featureFlags: state.features.featureFlags,
    studyHall: state.studyHall,
    licenseFetched: state.licenses.licenseFetched,
    fetchingLicenses: state.licenses.fetchingLicenses,
    isAuthed: state.users.isAuthed,
    notFound: state.users.notFound,
    userLicenses: state.licenses.userlicenses,
    testSettings: state.testSettings,
    assessment: state.assessment
  }
}

const mapDispatchToProps = dispatch => {
  return {
    checkLogin: () => dispatch(checkLogin()),
    getUserLicenseRequest: () => dispatch(getUserLicenseRequest()),
    getUserLicenseFailure: error => dispatch(getUserLicenseFailure(error)),
    getAssessmentSets: (licenses, role) => {
      dispatch(assessmentActions.getAssessmentSets(licenses, role))
    },
    getUserLicenseSuccess: (licenses, role) =>
      dispatch(getUserLicenseSuccess(licenses, role)),
    checkAnnouncements: user => dispatch(checkAnnouncements(user)),
    _sendStatus: status => dispatch(setConnectStatus(status)),
    onlineListener: () =>
      dispatch({
        type: "ON_USER_ONLINE"
      }),
    offlineListener: () =>
      dispatch({
        type: "ON_USER_OFFLINE"
      }),
    getActiveTestSettings: (user, subscriptionsCallback) =>
      dispatch(
        testSettingActions.getActiveTestSettings(user, subscriptionsCallback)
      ),
    initAssessment: (
      assessmentId,
      studentId,
      user,
      location,
      userAssessmentId,
      userAssessment,
      history,
      callback
    ) =>
      dispatch(
        assessmentActions.initAssessment(
          assessmentId,
          studentId,
          user,
          location,
          userAssessmentId,
          userAssessment,
          history,
          callback
        )
      )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App))
